import * as THREE from 'three'
import { AIMode, Direction, GridPos, JellyKind } from '../../types'
import { buildJellyfish, JellyModel } from '../../procedural/models'
import { GridMover } from '../GridMover'
import {
  chaserTarget,
  ambusherTarget,
  patrollerTarget,
  wandererTarget,
  chooseDirAtNode,
  randomDirAtNode,
} from '../ai-target'
import { ENEMY_SPEED, ENEMY_FRIGHTENED_SPEED } from '../../constants/game-config'
import { TAU, damp } from '../../utils/math'
import type { Maze } from '../world/Maze'

const EATEN_SPEED = 7.0

/** Cached warning-flash ON color (white). The OFF color is the model's own frightColor
 * (read from JellyModel) so it can never drift from the frightened bell color in models.ts. */
const WARNING_ON = new THREE.Color(0xffffff)

/** Body tilt [rotation.x, rotation.z] per heading so the bell leans into travel.
 * Up = -z (lean forward → +x tilt), Left = -x (lean → +z tilt). */
const LEAN: Record<Direction, [number, number]> = {
  [Direction.None]: [0, 0],
  [Direction.Up]: [0.2, 0],
  [Direction.Down]: [-0.2, 0],
  [Direction.Left]: [0, 0.2],
  [Direction.Right]: [0, -0.2],
}

export interface JellyContext {
  playerCell: GridPos
  playerDir: Direction
  chaserCell: GridPos
}

/** A jellyfish enemy with one of four Pac-Man-accurate personalities. */
export class Jellyfish {
  readonly group = new THREE.Group()
  readonly kind: JellyKind
  mode: AIMode = 'scatter'
  private model: JellyModel
  private mover: GridMover
  private maze: Maze
  private scatterCorner: GridPos
  private home: GridPos
  private start: GridPos
  private releaseTimer = 0
  private bobT = Math.random() * 10
  private swimPhase = Math.random() * TAU
  private leanX = 0
  private leanZ = 0
  private baseY = 0.1
  private baseSpeed = ENEMY_SPEED

  constructor(
    kind: JellyKind,
    start: GridPos,
    scatterCorner: GridPos,
    home: GridPos,
    maze: Maze,
    releaseDelay = 0,
  ) {
    this.kind = kind
    this.maze = maze
    this.scatterCorner = scatterCorner
    this.home = home
    this.start = start
    this.model = buildJellyfish(kind)
    this.group.add(this.model.group)
    this.mover = new GridMover(start.row, start.col, ENEMY_SPEED, maze, true)
    this.releaseTimer = releaseDelay
    this.sync()
  }

  get cell(): GridPos {
    return { row: this.mover.row, col: this.mover.col }
  }
  get worldX(): number {
    return this.mover.x
  }
  get worldZ(): number {
    return this.mover.z
  }
  get isEdible(): boolean {
    return this.mode === 'frightened'
  }
  get isEaten(): boolean {
    return this.mode === 'eaten'
  }

  reset(releaseDelay = 0): void {
    this.mover.setCell(this.start.row, this.start.col)
    this.mover.dir = Direction.None
    this.mode = 'scatter'
    this.model.setFrightened(false)
    this.restoreBellShape()
    ;(this.model.bell.material as THREE.MeshPhysicalMaterial).opacity = 0.92
    this.model.tentacles.rotation.set(0, 0, 0)
    this.model.group.rotation.set(0, 0, 0)
    this.leanX = this.leanZ = 0
    this.model.group.visible = true
    this.mover.speed = this.baseSpeed
    this.releaseTimer = releaseDelay
    this.sync()
  }

  setBaseSpeed(s: number): void {
    this.baseSpeed = s
    if (this.mode !== 'frightened' && this.mode !== 'eaten') this.mover.speed = s
  }

  /** Global scatter/chase wave — ignored while frightened or eaten. */
  setGlobalMode(mode: 'scatter' | 'chase'): void {
    if (this.mode === 'frightened' || this.mode === 'eaten') return
    this.mode = mode
  }

  frighten(): void {
    if (this.mode === 'eaten') return
    this.mode = 'frightened'
    this.model.setFrightened(true)
    this.mover.speed = ENEMY_FRIGHTENED_SPEED
  }

  unfrighten(globalMode: 'scatter' | 'chase'): void {
    if (this.mode !== 'frightened') return
    this.mode = globalMode
    this.model.setFrightened(false)
    this.mover.speed = this.baseSpeed
  }

  capture(): void {
    this.mode = 'eaten'
    this.model.setFrightened(false) // restores bell color/emissive to baseColor
    this.restoreBellShape() // clear any mid-contraction squash / warning flash
    this.model.group.visible = true
    this.mover.speed = EATEN_SPEED
    // dim while returning home
    ;(this.model.bell.material as THREE.MeshPhysicalMaterial).opacity = 0.35
  }

  private restoreFromEaten(globalMode: 'scatter' | 'chase'): void {
    this.mode = globalMode
    this.mover.speed = this.baseSpeed
    this.restoreBellShape()
    ;(this.model.bell.material as THREE.MeshPhysicalMaterial).opacity = 0.92
  }

  /** Reset the bell to its neutral rest scale + emissive so a respawn never inherits a
   * mid-animation squash or a stale frightened-glow value. Color is owned by setFrightened. */
  private restoreBellShape(): void {
    this.model.bell.scale.set(1, 0.85, 1)
    ;(this.model.bell.material as THREE.MeshPhysicalMaterial).emissiveIntensity = this.model.bellBaseEmissive
  }

  update(dt: number, ctx: JellyContext, globalMode: 'scatter' | 'chase'): void {
    if (this.releaseTimer > 0) {
      this.releaseTimer -= dt
      this.idleAnim(dt)
      this.sync()
      return
    }

    if (this.mode === 'eaten' && this.cell.row === this.home.row && this.cell.col === this.home.col) {
      this.restoreFromEaten(globalMode)
    }

    const isWalkable = (r: number, c: number) => this.maze.isWalkable(r, c, true)

    this.mover.update(dt, (cell, curDir) => {
      if (this.mode === 'frightened') {
        return randomDirAtNode(cell, curDir, isWalkable)
      }
      let target: GridPos
      if (this.mode === 'eaten') target = this.home
      else if (this.mode === 'scatter') target = this.scatterCorner
      else target = this.chaseTarget(cell, ctx)
      return chooseDirAtNode(cell, curDir, target, isWalkable)
    })

    this.animate(dt)
    this.sync()
  }

  private chaseTarget(self: GridPos, ctx: JellyContext): GridPos {
    switch (this.kind) {
      case 'chaser':
        return chaserTarget(ctx.playerCell)
      case 'ambusher':
        return ambusherTarget(ctx.playerCell, ctx.playerDir, 4)
      case 'patroller':
        return patrollerTarget(ctx.playerCell, ctx.playerDir, ctx.chaserCell, 2)
      case 'wanderer':
        return wandererTarget(self, ctx.playerCell, this.scatterCorner, 8)
    }
  }

  /** Ambient bobbing without AI/movement (attract + menu backdrops). */
  idle(dt: number): void {
    this.idleAnim(dt)
    const pulse = 1 + Math.sin(this.bobT * 5) * 0.05
    this.model.bell.scale.set(pulse, 0.85 / pulse, pulse)
    this.model.tentacles.rotation.z = Math.sin(this.bobT * 3) * 0.1
  }

  private idleAnim(dt: number): void {
    this.bobT += dt
    this.model.group.position.y = this.baseY + Math.sin(this.bobT * 2) * 0.08
  }

  /** Jet-propulsion swim: the bell contracts to push (squash wide + a little upward jet),
   * tentacles trail and undulate out of phase with the contraction, and the whole body leans
   * into its travel direction. Pulse rate + lean scale with movement speed, so a fast frightened
   * dash visibly swims harder than an idle drift. */
  private animate(dt: number): void {
    this.bobT += dt
    const moving = this.mover.dir !== Direction.None
    const speedRatio = this.mover.speed / ENEMY_SPEED

    // contraction wave: faster (harder jetting) the faster it travels
    const pulseRate = (moving ? 6 + speedRatio * 3 : 3.2) * dt
    this.swimPhase += pulseRate
    // Bound float growth. The per-tentacle term uses sin(swimPhase * 1.4); 1.4 = 7/5, so the
    // joint period of sin(x) and sin(1.4x) is 5*TAU — wrapping there leaves every strand's
    // phase continuous (no visible hitch) while keeping the accumulator small.
    if (this.swimPhase > TAU * 5) this.swimPhase -= TAU * 5
    if (this.bobT > TAU) this.bobT -= TAU // bobT only feeds integer-multiple sines
    const contract = Math.sin(this.swimPhase) // -1..1

    // bell squash: contracting flattens + widens the bell (jet), relaxing makes it tall
    const squash = 1 - contract * 0.18
    this.model.bell.scale.set(1 / squash, 0.85 * squash, 1 / squash)

    // vertical jet bob synced to the contraction (bigger thrust while moving)
    this.model.group.position.y = this.baseY + contract * (moving ? 0.12 : 0.06)

    // tentacles trail opposite the contraction, with each strand undulating on its own phase.
    // Clear any residual group-z lean left by idle() so a menu→play transition doesn't carry over.
    this.model.tentacles.rotation.x = -contract * 0.35
    this.model.tentacles.rotation.z = 0
    const tents = this.model.tentacles.children
    for (let i = 0; i < tents.length; i++) {
      tents[i].rotation.z = Math.sin(this.swimPhase * 1.4 + i * 0.8) * 0.18
    }

    // lean into the direction of travel (damped so turns ease rather than snap)
    const [tx, tz] = moving ? LEAN[this.mover.dir] : [0, 0]
    this.leanX = damp(this.leanX, tx * speedRatio, 6, dt)
    this.leanZ = damp(this.leanZ, tz * speedRatio, 6, dt)
    this.model.group.rotation.x = this.leanX
    this.model.group.rotation.z = this.leanZ

    const bellMat = this.model.bell.material as THREE.MeshPhysicalMaterial
    if (this.mode === 'frightened') {
      const f = (Math.sin(this.bobT * 10) * 0.5 + 0.5) * 0.4 + 0.3
      bellMat.emissiveIntensity = f
    } else if (bellMat.emissiveIntensity !== this.model.bellBaseEmissive) {
      // Pin to baseline so a frame that exits frightened (or skips via the release-timer
      // early-return) can't leave the bell stuck at a frightened-glow value.
      bellMat.emissiveIntensity = this.model.bellBaseEmissive
    }
  }

  /** Current swim state for diagnostics / the E2E harness. */
  get animState(): { bellScaleY: number; tentacleSway: number } {
    return { bellScaleY: this.model.bell.scale.y, tentacleSway: this.model.tentacles.rotation.x }
  }

  /** Brief end-of-power flash to warn the player. */
  setWarning(on: boolean): void {
    if (this.mode !== 'frightened') return
    // OFF restores the frightened bell color (read from the model so it can't drift).
    ;(this.model.bell.material as THREE.MeshPhysicalMaterial).color.copy(on ? WARNING_ON : this.model.frightColor)
  }

  private sync(): void {
    this.group.position.x = this.mover.x
    this.group.position.z = this.mover.z
  }
}
